import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Loader from 'components/atoms/loader';
import BiddingAttachmentList from 'components/molecules/bidding_attachment_list';
import colors from 'styles/colors';
import {
  BiddingDatas,
  BiddingResult,
  fmtBRL,
  fmtDataBR,
  fmtInt,
  linkLicitacao,
  priceIntelligenceApi,
  SearchHit,
  unidadeLabel,
} from 'services/price_intelligence';
import { Body } from './styles';

interface PriceIntelligenceBiddingModalProps {
  hit: SearchHit | null;
  onClose: () => void;
}

interface CampoProps {
  col: string;
  label: string;
  valor: string | number | null | undefined;
}

const cnpjParam = (cnpj?: string | number | null): string | null => {
  const limpo = String(cnpj || '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '');
  if (!limpo) return null;
  return limpo.length === 13 ? `0${limpo}` : limpo;
};

const Campo = ({ col, label, valor }: CampoProps) => (
  <div className={col}>
    <span className="campo-label">{label}</span>
    <p className="campo-valor">{valor || '—'}</p>
  </div>
);

const DATAS: Array<{ campo: keyof BiddingDatas; label: string }> = [
  { campo: 'datahora_abertura', label: 'Abertura' },
  { campo: 'datahora_prazo', label: 'Prazo' },
  { campo: 'datahora_documento', label: 'Documento' },
  { campo: 'datahora_retirada', label: 'Retirada' },
  { campo: 'datahora_visita', label: 'Visita' },
];

const fmtDataHora = (iso: string): string => {
  const hora = iso.slice(11, 16);
  return `${fmtDataBR(iso)}${/^\d{2}:\d{2}$/.test(hora) ? ` ${hora}` : ''}`;
};

const PriceIntelligenceBiddingModal = ({ hit, onClose }: PriceIntelligenceBiddingModalProps) => {
  const [licitacao, setLicitacao] = useState<BiddingResult | null>(null);
  const [carregando, setCarregando] = useState<boolean>(false);
  const [erro, setErro] = useState<string | null>(null);
  const licitacaoId = hit ? hit.payload.licitacao_id : undefined;
  const itemId = hit ? hit.payload.id_original : undefined;

  useEffect(() => {
    if (!licitacaoId) {
      setLicitacao(null);
      setErro(null);
      return undefined;
    }
    let ativo = true;

    const carregar = async () => {
      setLicitacao(null);
      setErro(null);
      setCarregando(true);
      try {
        const data = await priceIntelligenceApi.getBidding(licitacaoId);
        if (!ativo) return;
        if (data) setLicitacao(data);
        else setErro('Não encontramos o resultado desta licitação.');
      } catch (e) {
        if (ativo) setErro('Não foi possível carregar o resultado. Tente novamente.');
      } finally {
        if (ativo) setCarregando(false);
      }
    };

    carregar();
    return () => {
      ativo = false;
    };
  }, [licitacaoId]);

  const amostra = hit ? hit.payload : null;
  const edital = hit && hit.edital && hit.edital.disponivel ? hit.edital : null;
  const marcaFabricante = amostra
    ? [amostra.marca, amostra.fabricante].filter(Boolean).join(' / ')
    : '';
  const dados = licitacao ? licitacao.licitacao : null;
  const orgao = licitacao ? licitacao.orgao : null;
  const resultados = (licitacao && licitacao.resultados) || [];
  const acompanhamentos = (licitacao && licitacao.acompanhamentos) || [];
  const datas = dados
    ? DATAS.map((d) => ({ label: d.label, valor: dados.datas ? dados.datas[d.campo] : null }))
        .filter((d) => Boolean(d.valor))
        .map((d) => ({ label: d.label, valor: fmtDataHora(String(d.valor)) }))
    : [];
  const anexos = dados && dados.edital_status_id === 3 ? dados.edicts || [] : [];
  const linkCompleto = linkLicitacao(licitacaoId);

  return (
    <Modal size="lg" show={Boolean(hit)} onHide={onClose} centered>
      <div className="modal-header" style={{ backgroundColor: colors.$dark_light }}>
        <h5 className="modal-title text-white font-weight-bold">Detalhes da amostra</h5>
        <button aria-label="Fechar" className="close text-white" type="button" onClick={onClose}>
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <Body className="modal-body">
        {hit && amostra && (
          <>
            <p className="amostra-desc">{amostra.item_descricao || '—'}</p>
            <div className="amostra-cards">
              <div>
                <span>Valor unitário</span>
                <strong>
                  {typeof amostra.item_valor_unitario === 'number'
                    ? fmtBRL(amostra.item_valor_unitario)
                    : '—'}
                </strong>
              </div>
              <div>
                <span>Quantidade</span>
                <strong>
                  {typeof amostra.item_quantidade === 'number'
                    ? `${fmtInt(amostra.item_quantidade)} ${
                        hit.item_unidade_norm || amostra.item_unidade || ''
                      }`
                    : '—'}
                </strong>
              </div>
              <div>
                <span>Valor total</span>
                <strong>
                  {typeof amostra.item_valor_total === 'number'
                    ? fmtBRL(amostra.item_valor_total)
                    : '—'}
                </strong>
              </div>
              {hit.preco_normalizado && hit.unidade_preco && (
                <div>
                  <span>Preço normalizado</span>
                  <strong>
                    {`${fmtBRL(hit.preco_normalizado)}/${unidadeLabel(hit.unidade_preco)}`}
                  </strong>
                </div>
              )}
            </div>
            {(marcaFabricante || amostra.modelo) && (
              <div className="row">
                <Campo col="col-md-6" label="Marca / fabricante" valor={marcaFabricante} />
                <Campo col="col-md-6" label="Modelo" valor={amostra.modelo} />
              </div>
            )}
            {(amostra.item_numero || amostra.item_descricao_detalhado) && (
              <div className="row">
                {amostra.item_numero && (
                  <Campo col="col-md-2" label="Nº do item" valor={amostra.item_numero} />
                )}
                {amostra.item_descricao_detalhado &&
                  amostra.item_descricao_detalhado !== amostra.item_descricao && (
                    <Campo
                      col={amostra.item_numero ? 'col-md-10' : 'col-12'}
                      label="Descrição detalhada"
                      valor={amostra.item_descricao_detalhado}
                    />
                  )}
              </div>
            )}
            {edital && (
              <div className="amostra-edital">
                <p className="amostra-edital-title">
                  Edital da licitação
                  {hit.confirmado_pelo_edital && (
                    <span>
                      <i className="material-icons">verified</i>
                      especificação confirmada pela IA no texto do edital
                    </span>
                  )}
                </p>
                <div className="row">
                  <Campo col="col-md-4" label="Nº do edital" valor={edital.numero} />
                  <Campo col="col-md-8" label="Referência no edital" valor={edital.item_edital} />
                </div>
                {edital.trecho && <Campo col="" label="Trecho do edital" valor={edital.trecho} />}
              </div>
            )}
            {licitacaoId && <hr />}
          </>
        )}
        {carregando && <Loader />}
        {erro && <div className="alert alert-warning mb-0">{erro}</div>}
        {dados && (
          <>
            <div className="row">
              <Campo col="col-12" label="Objeto" valor={dados.objeto} />
            </div>
            <div className="row">
              <div className="col-md-4">
                <span className="campo-label">Datas</span>
                <p className="campo-valor">
                  {datas.length
                    ? datas.map((d) => (
                        <span key={d.label} className="d-block">
                          {d.label}: {d.valor}
                        </span>
                      ))
                    : 'Não informadas'}
                </p>
              </div>
              <Campo col="col-md-4" label="Nº ConLicitação" valor={dados.id} />
              <Campo col="col-md-4" label="Situação" valor={dados.situacao} />
            </div>
            <div className="row">
              <Campo col="col-md-4" label="Edital" valor={dados.edital} />
              <Campo col="col-md-8" label="Processo" valor={dados.processo} />
            </div>
            {dados.observacao && (
              <div className="row">
                <Campo col="col-12" label="Observação" valor={dados.observacao} />
              </div>
            )}
            {anexos.length > 0 && (
              <div className="anexos">
                <span className="campo-label">Anexos</span>
                <BiddingAttachmentList
                  biddingStatusEdital={dados.edital_status_id || 0}
                  biddingFiles={anexos}
                  omitSolicitation
                />
              </div>
            )}
            <hr />
            <div className="row">
              <Campo col="col-md-8" label="Órgão" valor={orgao && orgao.nome} />
              <Campo col="col-md-4" label="UASG" valor={orgao && orgao.uasg} />
            </div>
            <div className="row">
              <Campo col="col-md-6" label="Cidade" valor={orgao && orgao.cidade_uf} />
              <Campo col="col-md-6" label="Endereço" valor={orgao && orgao.endereco} />
            </div>
            {orgao && orgao.site && (
              <div className="row">
                <Campo col="col-12" label="Site" valor={orgao.site} />
              </div>
            )}
            <hr />
            <p className="secao-titulo">
              Resultado da licitação ({fmtInt(resultados.length)}{' '}
              {resultados.length === 1 ? 'item' : 'itens'})
            </p>
            {resultados.length === 0 && (
              <p className="campo-valor">
                Nenhum item de resultado registrado para esta licitação.
              </p>
            )}
            <div className="table-responsive resultados">
              <table className="table table-hover table-bordered mb-0">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Vencedor</th>
                    <th>Descrição</th>
                    <th>Un.</th>
                    <th>Qnt.</th>
                    <th>Valor unitário</th>
                    <th>Valor total</th>
                  </tr>
                </thead>
                <tbody>
                  {resultados.map((r) => {
                    const cnpj = cnpjParam(r.cnpj);
                    return (
                      <tr key={r.id} className={r.id === itemId ? 'destaque' : ''}>
                        <td>{r.item_numero || '—'}</td>
                        <td>
                          {cnpj ? (
                            <a
                              href={`/concorrente/${cnpj}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {r.fornecedor || cnpj}
                            </a>
                          ) : (
                            r.fornecedor || '—'
                          )}
                        </td>
                        <td>{r.item_descricao || '—'}</td>
                        <td>{r.item_unidade || '—'}</td>
                        <td>{typeof r.item_quantidade === 'number' ? r.item_quantidade : '—'}</td>
                        <td className="text-right">
                          {r.item_valor_unitario ? fmtBRL(r.item_valor_unitario) : '—'}
                        </td>
                        <td className="text-right">
                          {r.item_valor_total ? fmtBRL(r.item_valor_total) : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {acompanhamentos.length > 0 && (
              <>
                <hr />
                <p className="secao-titulo">Acompanhamentos ({fmtInt(acompanhamentos.length)})</p>
                <div className="table-responsive resultados">
                  <table className="table table-hover table-bordered mb-0">
                    <thead>
                      <tr>
                        <th>Síntese</th>
                        <th>Data da fonte</th>
                      </tr>
                    </thead>
                    <tbody>
                      {acompanhamentos.map((a) => (
                        <tr key={a.id}>
                          <td>{a.sintese || '—'}</td>
                          <td className="text-nowrap">{a.data_fonte || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}
      </Body>
      <div className="modal-footer">
        {linkCompleto && (
          <a
            className="btn btn-outline-primary"
            href={linkCompleto}
            target="_blank"
            rel="noopener noreferrer"
          >
            Abrir licitação completa
          </a>
        )}
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Fechar
        </button>
      </div>
    </Modal>
  );
};

export default PriceIntelligenceBiddingModal;
